import { z } from 'zod';

export const TicketTypeSchema = z.enum([
  'docs',
  'task',
  'bug',
  'feature',
  'master',
  'mission',
  'practice',
  'test',
  'ai',
  'lab',
  'design',
  'metrics',
  'exam',
  // Homework variant · slot_homework_content_engineering HW.3 · 18 MAY 2026.
  // Adapter в shared/data/tickets/index.ts wraps Homework entries (src/data/homework.ts)
  // в TicketMeta-shaped objects with type='homework' so /ticket/:id routing works
  // through existing ticketById() resolver. See Ticket.tsx homework branch для render.
  'homework',
]);

export const TicketStatusSchema = z.enum([
  'backlog',
  'todo',
  'in-progress',
  'review',
  'done',
  'locked',
]);

export const TicketPrioritySchema = z.enum(['P0', 'P1', 'P2', 'P3']);

export const ISTQBChapterRefSchema = z.enum([
  'CTFL-Ch1',
  'CTFL-Ch2',
  'CTFL-Ch3',
  'CTFL-Ch4',
  'CTFL-Ch5',
  'CTFL-Ch6',
]);

export const AcceptanceCriterionSchema = z.object({
  id: z.string(),
  text: z.string(),
  hint: z.string().optional(),
});

export const QuickCheckOptionSchema = z.object({
  id: z.string(),
  text: z.string(),
});

export const QuickCheckSchema = z.object({
  id: z.string(),
  prompt: z.string(),
  question: z.string(),
  options: z.array(QuickCheckOptionSchema),
  answer: z.string(),
  explanation: z.string(),
});

/**
 * Mission · dual-pattern (Genie ack 2026-05-02 · option a · backwards-compat extend).
 * CTA-mission: `cta` + `acceptanceText` (S01 DOC-pattern).
 * Anchor-mission: `sample` + optional `gwt` + `reflectionPrompts` (S02+ recovery pattern).
 * Renderer branches by field presence — Zod stays loose with all-optional fields.
 */
export const MissionSchema = z.object({
  cta: z.string().optional(),
  url: z.string().optional(),
  acceptanceText: z.string().optional(),
  sample: z.string().optional(),
  gwt: z
    .object({
      given: z.string(),
      when: z.string(),
      then: z.string(),
    })
    .optional(),
  reflectionPrompts: z.array(z.string()).optional(),
  /**
   * Phase 3.4 · ASK-3.4-3=A · Atelier scenario id for Mission tickets that
   * launch an interactive sub-app surface (e.g. bug-report end-to-end · SQL
   * playground). Renderer reads this and routes through `<AtelierFrame/>`
   * instead of opening `mission.url` as an external link. Purely additive.
   */
  atelierScenarioId: z.string().optional(),
});

export const GlossarySpanSchema = z.object({
  termKey: z.string(),
  surface: z.string(),
});

export const SeniorExtensionSchema = z.object({
  html: z.string(),
});

export const TicketContentRefSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('mdx'), path: z.string() }),
  z.object({ kind: z.literal('inline'), html: z.string() }),
  z.object({ kind: z.literal('markdown'), body: z.string() }),
]);

export const TicketMetaSchema = z.object({
  id: z.string(),
  sprintId: z.string(),
  ord: z.number().int(),
  type: TicketTypeSchema,
  priority: TicketPrioritySchema,
  title: z.string(),
  subtitle: z.string().optional(),
  estimateMin: z.number().int(),
  ac: z.array(AcceptanceCriterionSchema),
  istqb: z.array(ISTQBChapterRefSchema),
  skills: z.array(z.string()),
  quickCheck: QuickCheckSchema.optional(),
  mission: MissionSchema.optional(),
  glossarySpans: z.array(GlossarySpanSchema).optional(),
  seniorExtension: SeniorExtensionSchema.optional(),
  capstoneGate: z.boolean().optional(),
});

export const TicketSchema = TicketMetaSchema.extend({
  contentRef: TicketContentRefSchema,
});

export type TicketType = z.infer<typeof TicketTypeSchema>;
export type TicketStatus = z.infer<typeof TicketStatusSchema>;
export type TicketPriority = z.infer<typeof TicketPrioritySchema>;
export type ISTQBChapterRef = z.infer<typeof ISTQBChapterRefSchema>;
export type AcceptanceCriterion = z.infer<typeof AcceptanceCriterionSchema>;
export type QuickCheckOption = z.infer<typeof QuickCheckOptionSchema>;
export type QuickCheck = z.infer<typeof QuickCheckSchema>;
export type Mission = z.infer<typeof MissionSchema>;
export type GlossarySpan = z.infer<typeof GlossarySpanSchema>;
export type SeniorExtension = z.infer<typeof SeniorExtensionSchema>;
export type TicketContentRef = z.infer<typeof TicketContentRefSchema>;
export type TicketMeta = z.infer<typeof TicketMetaSchema>;
export type Ticket = z.infer<typeof TicketSchema>;
